import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowLeft, UserPlus, Users } from 'lucide-react'
import { fetchCustomers } from '@/api/customers'
import {
  createUser,
  deactivateUser,
  deleteUser,
  fetchUsers,
  updateUser,
} from '@/api/users'
import { useAuth } from '@/auth/AuthContext'
import { CustomerFilter } from '@/components/CustomerFilter'
import { HorizontalScrollArea } from '@/components/HorizontalScrollArea'
import { CollapseSection } from '@/components/ui/CollapseSection'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

function UsersTile({ to, title, description, icon: Icon }) {
  return (
    <Link
      to={to}
      className={cn(
        'rounded-xl border border-border bg-card p-6 shadow-sm transition',
        'hover:border-primary/40 hover:bg-card hover:shadow-md',
      )}
    >
      <div className="mb-4">
        <Icon className="h-7 w-7 text-primary" strokeWidth={1.75} aria-hidden />
      </div>
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{description}</p>
    </Link>
  )
}

function UsersOverview({ activeCount, totalCount }) {
  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card px-6 py-8 shadow-sm sm:px-8">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          Users
        </h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Create portal accounts for administrators, staff, and customers. Customer
          accounts can be linked to one or more companies.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <UsersTile
          to="/admin/users?section=create"
          title="Create user"
          description="Add a new portal account with email, temporary password, and role."
          icon={UserPlus}
        />
        <UsersTile
          to="/admin/users?section=list"
          title="Portal users"
          description={
            totalCount
              ? `${totalCount} user${totalCount === 1 ? '' : 's'} · ${activeCount} active`
              : 'View accounts, reset passwords, and manage 2FA.'
          }
          icon={Users}
        />
      </div>
    </div>
  )
}

function SectionShell({ title, description, children }) {
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Button asChild variant="ghost" size="sm" className="-ml-2 h-8 px-2 text-muted-foreground">
          <Link to="/admin/users">
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back to Users
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6">{children}</div>
    </div>
  )
}

function CreateUserPanel({
  form,
  setForm,
  customers,
  saving,
  error,
  onSubmit,
  accountOpen,
  setAccountOpen,
  accessOpen,
  setAccessOpen,
}) {
  return (
    <SectionShell
      title="Create user"
      description="Add a new portal account. The password is temporary — the user must change it on first sign-in."
    >
      <form onSubmit={onSubmit} className="space-y-2">
        <CollapseSection
          title="Account details"
          subtitle="Email, name, and temporary password."
          open={accountOpen}
          onOpenChange={setAccountOpen}
        >
          <div className="grid max-w-lg gap-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Full name</Label>
              <Input
                required
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Temporary password</Label>
              <Input
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Share this securely. The user will be asked to set a new password on first sign-in.
              </p>
            </div>
          </div>
        </CollapseSection>

        <CollapseSection
          title="Access"
          subtitle="Role and customer companies for customer users."
          open={accessOpen}
          onOpenChange={setAccessOpen}
        >
          <div className="grid max-w-lg gap-4">
            <div className="space-y-2">
              <Label>Role</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-white px-3 text-sm"
                value={form.role}
                onChange={(e) =>
                  setForm({
                    ...form,
                    role: e.target.value,
                    customer_ids: e.target.value === 'customer' ? form.customer_ids : [],
                  })
                }
              >
                <option value="admin">Administrator</option>
                <option value="staff">Staff</option>
                <option value="customer">Customer</option>
              </select>
            </div>
            {form.role === 'customer' ? (
              <div className="space-y-2">
                <CustomerFilter
                  label="Customer companies"
                  customers={customers}
                  value={form.customer_ids}
                  onChange={(ids) => setForm({ ...form, customer_ids: ids })}
                  emptySummary="Select companies…"
                  clearLabel="Clear selection"
                  showFilterHint={false}
                />
                <p className="text-xs text-muted-foreground">
                  Select one or more companies. The user can only see data for these
                  companies.
                </p>
              </div>
            ) : null}
          </div>
        </CollapseSection>

        <div className="pt-2">
          <Button type="submit" disabled={saving}>
            {saving ? 'Creating…' : 'Create user'}
          </Button>
        </div>
      </form>
      {error ? <p className="mt-4 text-sm text-destructive">{error}</p> : null}
    </SectionShell>
  )
}

function mfaLabel(user, siteRequireMfa) {
  if (user.totp_enabled) return 'Enrolled'
  if (user.mfa_exempt) return 'Off (testing)'
  return siteRequireMfa ? 'Required' : 'Off'
}

function companyLabel(user) {
  if (user.role !== 'customer') return '—'
  const names = user.customer_names?.length
    ? user.customer_names
    : user.customer_name
      ? [user.customer_name]
      : []
  if (!names.length) return '—'
  if (names.length === 1) return names[0]
  return `${names[0]} +${names.length - 1}`
}

function UsersListPanel({
  users,
  customers,
  loading,
  error,
  siteRequireMfa,
  editingId,
  editIds,
  setEditIds,
  setEditingId,
  savingEdit,
  onSaveCompanies,
  onDeactivate,
  onDelete,
  onTurnOff2fa,
  onRequire2faAgain,
  onResetPassword,
}) {
  const activeCount = users.filter((u) => u.is_active).length

  return (
    <SectionShell
      title="Portal users"
      description={`${users.length} user${users.length === 1 ? '' : 's'} · ${activeCount} active`}
    >
      {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}
      {loading ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <HorizontalScrollArea>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 whitespace-nowrap">Name</th>
                <th className="py-2">Email</th>
                <th className="py-2">Role</th>
                <th className="py-2">Customer</th>
                <th className="py-2">2FA</th>
                <th className="py-2">Status</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-border/60 align-top">
                  <td className="py-2 whitespace-nowrap">{u.full_name}</td>
                  <td className="py-2">{u.email}</td>
                  <td className="py-2 capitalize">{u.role}</td>
                  <td className="py-2 min-w-[220px]">
                    {editingId === u.id ? (
                      <div className="space-y-2 py-1">
                        <CustomerFilter
                          customers={customers}
                          value={editIds}
                          onChange={setEditIds}
                          emptySummary="Select companies…"
                          clearLabel="Clear selection"
                          showFilterHint={false}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            disabled={savingEdit || editIds.length === 0}
                            onClick={() => onSaveCompanies(u)}
                          >
                            {savingEdit ? 'Saving…' : 'Save'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingId(null)
                              setEditIds([])
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <span title={(u.customer_names || []).join(', ')}>
                          {companyLabel(u)}
                        </span>
                        {u.role === 'customer' && u.is_active ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="ml-1 h-7 px-2"
                            onClick={() => {
                              setEditingId(u.id)
                              setEditIds(
                                Array.isArray(u.customer_ids) && u.customer_ids.length
                                  ? u.customer_ids
                                  : u.customer_id
                                    ? [u.customer_id]
                                    : [],
                              )
                            }}
                          >
                            Edit
                          </Button>
                        ) : null}
                      </div>
                    )}
                  </td>
                  <td className="py-2 whitespace-nowrap">
                    <span className="mr-2">{mfaLabel(u, siteRequireMfa)}</span>
                    {u.totp_enabled || !u.mfa_exempt ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => onTurnOff2fa(u)}
                      >
                        Turn off
                      </Button>
                    ) : null}
                    {u.mfa_exempt ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => onRequire2faAgain(u)}
                      >
                        Require again
                      </Button>
                    ) : null}
                  </td>
                  <td className="py-2">
                    <Badge variant={u.is_active ? 'success' : 'secondary'}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {u.must_change_password ? (
                      <Badge variant="secondary" className="ml-1">
                        Temp password
                      </Badge>
                    ) : null}
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {u.is_active ? (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onResetPassword(u)}
                        >
                          Reset password
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => onDeactivate(u.id)}>
                          Deactivate
                        </Button>
                      </>
                    ) : null}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => onDelete(u)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </HorizontalScrollArea>
      )}
    </SectionShell>
  )
}

export function UserManagement() {
  const { requireMfa: siteRequireMfa } = useAuth()
  const [params] = useSearchParams()
  const section = params.get('section')
  const [users, setUsers] = useState([])
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [accountOpen, setAccountOpen] = useState(true)
  const [accessOpen, setAccessOpen] = useState(false)
  const [form, setForm] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'staff',
    customer_ids: [],
  })
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editIds, setEditIds] = useState([])
  const [savingEdit, setSavingEdit] = useState(false)

  function load() {
    return fetchUsers().then(setUsers)
  }

  useEffect(() => {
    Promise.all([load(), fetchCustomers().then(setCustomers)])
      .catch(() => setError('Could not load users.'))
      .finally(() => setLoading(false))
  }, [])

  async function handleCreate(e) {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      if (form.role === 'customer' && form.customer_ids.length === 0) {
        setError('Select at least one customer company.')
        setSaving(false)
        return
      }
      await createUser({
        email: form.email,
        password: form.password,
        full_name: form.full_name,
        role: form.role,
        customer_ids: form.role === 'customer' ? form.customer_ids.map(Number) : null,
      })
      setForm({
        email: '',
        password: '',
        full_name: '',
        role: 'staff',
        customer_ids: [],
      })
      await load()
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not create user.')
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveCompanies(u) {
    setSavingEdit(true)
    setError('')
    try {
      await updateUser(u.id, { customer_ids: editIds.map(Number) })
      setEditingId(null)
      setEditIds([])
      await load()
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not update companies.')
    } finally {
      setSavingEdit(false)
    }
  }

  async function handleDeactivate(id) {
    if (!confirm('Deactivate this user? They will no longer be able to sign in.')) return
    try {
      await deactivateUser(id)
      await load()
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not deactivate user.')
    }
  }

  async function handleDelete(u) {
    if (
      !confirm(
        `Permanently delete ${u.email}? This cannot be undone. Prefer Deactivate if you only want to block sign-in.`,
      )
    ) {
      return
    }
    try {
      await deleteUser(u.id)
      await load()
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not delete user.')
    }
  }

  async function handleTurnOff2fa(u) {
    if (
      !confirm(
        `Turn off 2FA for ${u.email}? They can sign in with password only until you require 2FA again.`,
      )
    ) {
      return
    }
    try {
      await updateUser(u.id, { totp_enabled: false, mfa_exempt: true })
      await load()
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not turn off 2FA.')
    }
  }

  async function handleRequire2faAgain(u) {
    if (!confirm(`Require 2FA again for ${u.email}? They must enrol an authenticator after sign-in.`)) {
      return
    }
    try {
      await updateUser(u.id, { mfa_exempt: false })
      await load()
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not update 2FA requirement.')
    }
  }

  async function handleResetPassword(u) {
    const password = window.prompt(
      `Enter a temporary password for ${u.email} (min 8 characters). They must change it on next sign-in.`,
    )
    if (!password) return
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    try {
      await updateUser(u.id, { password })
      await load()
      window.alert('Temporary password set. Share it securely with the user.')
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not reset password.')
    }
  }

  const activeCount = users.filter((u) => u.is_active).length

  if (section === 'create') {
    return (
      <CreateUserPanel
        form={form}
        setForm={setForm}
        customers={customers}
        saving={saving}
        error={error}
        onSubmit={handleCreate}
        accountOpen={accountOpen}
        setAccountOpen={setAccountOpen}
        accessOpen={accessOpen}
        setAccessOpen={setAccessOpen}
      />
    )
  }

  if (section === 'list') {
    return (
      <UsersListPanel
        users={users}
        customers={customers}
        loading={loading}
        error={error}
        siteRequireMfa={siteRequireMfa}
        editingId={editingId}
        editIds={editIds}
        setEditIds={setEditIds}
        setEditingId={setEditingId}
        savingEdit={savingEdit}
        onSaveCompanies={handleSaveCompanies}
        onDeactivate={handleDeactivate}
        onDelete={handleDelete}
        onTurnOff2fa={handleTurnOff2fa}
        onRequire2faAgain={handleRequire2faAgain}
        onResetPassword={handleResetPassword}
      />
    )
  }

  return (
    <UsersOverview activeCount={activeCount} totalCount={loading ? 0 : users.length} />
  )
}
